import "./GratuityForm.scss";

import React from "react";
import { Link, Redirect } from "react-router-dom";

import request from "request"
import usesFeatures from "usesFeatures"

import Header from "Header"
import Form from "Form"
import Input from "Input"
import Select from "Select"
import Switch from "Switch"
import StoreTerminalSelector from "StoreTerminalSelector"

class GratuityForm extends React.Component {
  constructor (props) {
    super(props)
    this.state = { errors: {}, serverErrors: [] }
    this.serverErrorRef = React.createRef()
  }

  // Bring the server-error banner into view and move keyboard focus to it so
  // the user (and screen readers) land on the explanation.
  focusServerErrors = () => {
    const el = this.serverErrorRef.current
    if (!el) return
    if (typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
    el.focus()
  }

  // Client-side validation mirroring the Rails model: name required, amount
  // required and positive, percentage capped at 100. Returns an errors object.
  validate = () => {
    const { formData } = this.props
    const errors = {}

    if (!formData.name || !formData.name.trim()) {
      errors.name = "Name is required"
    }

    const amount = parseFloat(formData.gratuityAmount)
    if (formData.gratuityAmount === undefined || formData.gratuityAmount === null || formData.gratuityAmount === "") {
      errors.gratuityAmount = "Amount is required"
    } else if (!Number.isFinite(amount) || amount <= 0) {
      errors.gratuityAmount = "Amount must be greater than zero"
    } else if ((formData.gratuityUnit || "percent") === "percent" && amount > 100) {
      errors.gratuityAmount = "Percentage cannot exceed 100"
    }

    return errors
  }

  componentDidMount() {
    const gratuityId = this.props.computedMatch.params.gratuityId
    if (gratuityId) {
      this.props.fetchData(`venue_admin/gratuities/${gratuityId}/edit`, this.seedForm)
    } else {
      this.props.fetchData("venue_admin/gratuities/new", this.seedForm)
    }
  }

  headerText = () => this.props.formData.id ? "Edit Gratuity" : "New Gratuity"

  seedForm = (data) => this.props.seedFormData(data)

  // Threshold is stored in cents but edited in dollars.
  thresholdDollars = () => ((this.props.formData.autoApplyThresholdCents || 0) / 100) || ""

  setThresholdDollars = (raw) => {
    const dollars = parseFloat(raw)
    const cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0
    this.props.updateField("autoApplyThresholdCents", cents)
  }

  toggleEnabledStore = (storeId) => {
    const current = this.props.formData.enabledStoreIds || []
    const next = current.includes(storeId)
      ? current.filter((id) => id !== storeId)
      : [...current, storeId]
    this.props.updateField("enabledStoreIds", next)
  }

  handleSubmit = (e) => {
    e.preventDefault();
    const { formData } = this.props

    const errors = this.validate()
    if (Object.keys(errors).length > 0) {
      this.setState({ errors })
      return
    }
    this.setState({ errors: {} })

    const gratuityId = formData.id
    const method = gratuityId ? "patch" : "post"
    const url = gratuityId ? `gratuities/${gratuityId}` : "gratuities"

    const gratuity = {
      name: formData.name,
      gratuity_amount: formData.gratuityAmount,
      gratuity_unit: formData.gratuityUnit || "percent",
      taxable: !!formData.taxable,
      auto_apply: !!formData.autoApply,
      auto_apply_threshold_cents: formData.autoApplyThresholdCents || 0,
    }
    // enabled_store_ids = manual-menu visibility; store_ids/terminal_ids = auto scope.
    const enabled_store_ids = formData.enabledStoreIds || []
    const store_ids = formData.storeIds || []
    const terminal_ids = formData.terminalIds || []

    const req = request[method](`venue_admin/${url}`, { gratuity, enabled_store_ids, store_ids, terminal_ids })
    req.then(({data}) => {
      if (data.success) {
        this.setState({ saved: true, id: data.gratuity.id })
      } else {
        this.setState({ serverErrors: data.errors || [] }, this.focusServerErrors)
      }
    })
  }

  render() {
    if (!this.props.dataFetched) return false
    if (this.state.saved && this.state.id) return <Redirect to={`/gratuities/${this.state.id}`} />

    const { updateField, formData } = this.props
    const stores = this.props.stores || []
    const enabledStoreIds = new Set(formData.enabledStoreIds || [])

    return(
      <div className="GratuityForm">
        <Header
          text="Gratuities"
          backLink={`/gratuities${formData.id ? ("/" + formData.id) : ""}`}
        />
        <Form onSubmit={this.handleSubmit} title={this.headerText()}>
          { this.state.serverErrors.length > 0 &&
            <div
              className="notification is-danger"
              ref={this.serverErrorRef}
              tabIndex={-1}
              role="alert"
            >
              <ul>
                {this.state.serverErrors.map((message, i) => (
                  <li key={i}>{message}</li>
                ))}
              </ul>
            </div>
          }

          <Input
            label="Gratuity Name"
            onChange={updateField.bind(null, "name")}
            value={formData.name}
            errors={this.state.errors}
            errorKey="name"
          />

          <Input
            label="Amount"
            type="number"
            min={0}
            onChange={updateField.bind(null, "gratuityAmount")}
            value={formData.gratuityAmount}
            allowNegativeValue={false}
            errors={this.state.errors}
            errorKey="gratuityAmount"
          />

          <Select
            label="Gratuity Unit"
            options={{'percent': 'percent', 'dollar': 'dollar'}}
            onChange={updateField.bind(null, "gratuityUnit")}
            value={formData.gratuityUnit}
          />

          <Switch
            label="Taxable"
            value={!!formData.taxable}
            onChange={(v) => updateField("taxable", v)}
          />

          <div className="block">
            <label className="label">Enable in stores</label>
            <div className="form-note">
              Enabling this gratuity in a store makes it selectable by cashiers in
              the <strong>Discounts menu</strong> on that store's terminals. If you
              also set an automatic amount below, it is applied automatically to
              orders in these enabled stores. Automatic application to specific
              terminals is configured separately in the <em>automatic</em>{" "}
              section below, and works differently.
            </div>
            {stores.length === 0 ? (
              <p className="has-text-grey is-italic">No stores defined yet.</p>
            ) : (
              <div className="store-enable-list">
                {stores.map((store) => {
                  const checked = enabledStoreIds.has(store.id)
                  return (
                    <label key={store.id} className={`store-enable-chip ${checked ? "checked" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => this.toggleEnabledStore(store.id)}
                      />
                      {store.name}
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          <Switch
            label="Apply automatically"
            value={!!formData.autoApply}
            onChange={(v) => updateField("autoApply", v)}
          />

          { formData.autoApply &&
            <div className="auto-apply-config">
              <Input
                label="Apply when order subtotal is at least ($)"
                type="number"
                min={0}
                onChange={this.setThresholdDollars}
                value={this.thresholdDollars()}
              />

              <div className="form-note">
                When an amount is set, this gratuity is applied automatically to
                every order at or above that amount in the stores enabled above.
                Alternatively, select specific stores or terminals below to
                auto-apply only there (selecting one also enables this gratuity in
                that store). If you set <strong>both</strong> an amount and a store
                or terminal, both must be true (<strong>AND</strong>): the order
                must be at or above the amount <em>and</em> belong to a selected
                store or terminal.
              </div>

              <div className="block">
                <label className="label">Apply automatically at these stores / terminals</label>
                <StoreTerminalSelector
                  stores={stores}
                  storeIds={formData.storeIds || []}
                  terminalIds={formData.terminalIds || []}
                  onStoreIdsChange={(ids) => updateField("storeIds", ids)}
                  onTerminalIdsChange={(ids) => updateField("terminalIds", ids)}
                />
              </div>
            </div>
          }

          <div className="block field is-grouped mt-4">
            <p className="control">
              <button onClick={this.handleSubmit} className="button is-primary">{ formData.id ? "Update" : "Create"} Gratuity</button>
            </p>
            <p className="control">
              <Link to={ formData.id ? `/gratuities/${formData.id}` : "/gratuities"} className="button">Cancel</Link>
            </p>
          </div>
        </Form>
      </div>
    )
  }
}

export default usesFeatures(['form', 'data'], GratuityForm)
